import {Invoice} from '../types';
import {InvoiceItem} from '../../invoice-items/types';
import {
    Box,
    Divider,
    Group,
    Paper,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import {StatusBadge} from './StatusBadge';
import {formatTimestamp} from '../../../utils/TableTimestamps';
import {useInvoiceItems} from '../../invoice-items/hooks/useInvoiceItems';
import {PlatformLoader} from '@flexinet/ui-components';

const formatCurrency = (amount: number, currency: string) => {
    const value = (amount / 100).toFixed(2);
    const symbol = currency?.toUpperCase() || 'USD';
    return `${value} ${symbol}`;
};

export const InvoicePreview = ({record}: { record: Invoice }) => {

    const {items: lineItems, loading} = useInvoiceItems(record.id);

    return (
        <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Stack gap="lg">

                {/* Header */}
                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Title order={3}>INVOICE</Title>
                        <Text size="sm" c="dimmed">{record.number || record.id}</Text>
                    </Stack>
                    <StatusBadge status={record.status}/>
                </Group>

                <Divider/>

                {/* Bill To & Invoice Details */}
                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Text size="xs" fw={700} tt="uppercase" c="dimmed">Bill To</Text>
                        {record.customerName &&
                            <Text size="sm" fw={600}>{record.customerName}</Text>}
                        {record.customerEmail &&
                            <Text size="sm" c="dimmed">{record.customerEmail}</Text>}
                    </Stack>
                    <Stack gap={4} align="flex-end">
                        <Group gap="xs">
                            <Text size="xs" c="dimmed">Invoice Date:</Text>
                            <Text size="sm">{formatTimestamp(parseInt(record.created) * 1000, 'MMM DD, YYYY')}</Text>
                        </Group>
                        {record.periodStart && record.periodEnd && (
                            <Group gap="xs">
                                <Text size="xs" c="dimmed">Period:</Text>
                                <Text
                                    size="sm">{formatTimestamp(parseInt(record.periodStart) * 1000, 'MMM DD, YYYY')} - {formatTimestamp(parseInt(record.periodEnd) * 1000, 'MMM DD, YYYY')}</Text>
                            </Group>
                        )}
                        {record.collectionMethod && (
                            <Group gap="xs">
                                <Text size="xs" c="dimmed">Collection:</Text>
                                <Text size="sm">{record.collectionMethod === 'send_invoice' ? 'Send Invoice' : 'Charge Automatically'}</Text>
                            </Group>
                        )}
                        {record.daysUntilDue > 0 && (
                            <Group gap="xs">
                                <Text size="xs" c="dimmed">Payment Terms:</Text>
                                <Text size="sm">Net {record.daysUntilDue}</Text>
                            </Group>
                        )}
                    </Stack>
                </Group>

                <Divider/>

                {/* Line Items */}
                {loading ? (
                    <PlatformLoader message="Loading line items..."/>
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Description</Table.Th>
                                <Table.Th style={{textAlign: 'right'}}>Qty</Table.Th>
                                <Table.Th style={{textAlign: 'right'}}>Unit Price</Table.Th>
                                <Table.Th style={{textAlign: 'right'}}>Amount</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {lineItems.length > 0 ? lineItems.map((item: InvoiceItem) => (
                                <Table.Tr key={item.id}>
                                    <Table.Td>
                                        <Text size="sm">{item.description || item.id}</Text>
                                    </Table.Td>
                                    <Table.Td style={{textAlign: 'right'}}>
                                        <Text size="sm">{item.quantity || 1}</Text>
                                    </Table.Td>
                                    <Table.Td style={{textAlign: 'right'}}>
                                        <Text
                                            size="sm">{item.unitAmount ? formatCurrency(item.unitAmount, item.currency || record.currency) : '-'}</Text>
                                    </Table.Td>
                                    <Table.Td style={{textAlign: 'right'}}>
                                        <Text
                                            size="sm">{formatCurrency(item.amount, item.currency || record.currency)}</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )) : (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm" c="dimmed" ta="center">No line items</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                )}

                <Divider/>

                {/* Totals */}
                <Box>
                    <Stack gap={4} align="flex-end">
                        <Group gap="xl">
                            <Text size="sm" c="dimmed">Subtotal:</Text>
                            <Text size="sm" fw={500}>{formatCurrency(record.total, record.currency)}</Text>
                        </Group>
                        <Group gap="xl">
                            <Text size="sm" c="dimmed">Amount Paid:</Text>
                            <Text size="sm" fw={500}>{formatCurrency(record.amountPaid, record.currency)}</Text>
                        </Group>
                        <Divider w={250}/>
                        <Group gap="xl">
                            <Text size="md" fw={700}>Amount Due:</Text>
                            <Text size="md" fw={700}>{formatCurrency(record.amountDue, record.currency)}</Text>
                        </Group>
                    </Stack>
                </Box>

                {/* Description / Memo */}
                {record.description && (
                    <>
                        <Divider/>
                        <Stack gap={4}>
                            <Text size="xs" fw={700} tt="uppercase" c="dimmed">Memo</Text>
                            <Text size="sm">{record.description}</Text>
                        </Stack>
                    </>
                )}

            </Stack>
        </Paper>
    );
};
